import { useForm } from "react-hook-form";
import { useGetVisitaByIdQuery, useUpdateVisitInformationMutation } from "../../../../api/visitasApi";
import { useGetFrecuenciaFilterDataQuery } from "../../../../api/filter/dataFilterApi";
import { AvailableEmpresaFilters } from "../../../../utils/enums";
import { useState } from "react";
import { getErrorMessage } from "../../../../utils/errorHelpers";
import { ErrorMessage } from "../../../../components/errors/ErrorMessage";
import { ActionButtons } from "src/components/ui/ActionButtons";
import { FormContainer } from "./FormContainer";
import { LoadingScreen } from "src/components/ui/LoadingScreen";
import { FormInput, FormSelect } from "src/components/ui/inputs";
import { VisitInformation } from "../types";

interface Props {
    visitaId: number;
    minAvailableDate?: string;
    maxAvailableDate?: string;
    onCancel?: () => void;
}

export function EditVisitInfoForm({ visitaId, minAvailableDate, maxAvailableDate, onCancel }: Props) {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { data: visita, isFetching: visitaIsFetching } = useGetVisitaByIdQuery({ visitaId, rawFormat: true });
    const { data: inputValues, isFetching: inputValuesIsFetching } = useGetFrecuenciaFilterDataQuery([AvailableEmpresaFilters.Turnos]);

    const [updateVisit, { isLoading }] = useUpdateVisitInformationMutation();

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty }
    } = useForm<VisitInformation>({
        values: visita
    });

    const handleOnSubmit = handleSubmit(async (data) => {
        try {
            setErrorMessage(null);

            await updateVisit({ visitaId, data }).unwrap();

            onCancel?.();
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    });

    if(visitaIsFetching || inputValuesIsFetching)
        return (
            <FormContainer>
                <LoadingScreen />
            </FormContainer>
        );

    return (
        <FormContainer>
            <p className="font-semibold text-lg pb-2">Editar información</p>

            <form onSubmit={handleOnSubmit}>
                <div className="flex gap-x-3 justify-between">
                    <div>
                        <FormInput
                            {...register("fecha", {
                                required: "La fecha de la visita es requerida",
                            })}
                            label="Fecha"
                            aria-invalid={errors.fecha ? "true" : "false"}
                            min={minAvailableDate}
                            max={maxAvailableDate}
                            type="date"/>

                        {errors.fecha?.type === "required" && (
                            <p className="valErrorText text-sm">{errors.fecha?.message}</p>
                        )}
                    </div>

                    <div>
                        <FormInput
                            {...register("horaInicio", {
                                required: "La hora de inicio es requerida",
                                validate: {
                                    horaInicioIsBeforeFin: (horaInicio, formValues) => {
                                        // turno es Nocturno (id 3)
                                        if (formValues.turnoId == 3) return true;

                                        if(formValues.horaFin === undefined ||
                                            formValues.horaFin < horaInicio)
                                            return false;

                                        return true;
                                    }
                                },
                            })}
                            label="Hora de inicio"
                            aria-invalid={errors.horaInicio ? "true" : "false"}
                            type="time"/>

                        {errors.horaInicio?.type === "required" && (
                            <p className="valErrorText text-sm">{errors.horaInicio?.message}</p>
                        )}
                        {errors.horaInicio?.type === "horaInicioIsBeforeFin" && (
                            <p className="valErrorText text-sm">La hora de inicio no puede ser mayor a la de fin</p>
                        )}
                    </div>

                    <div>
                        <FormInput
                            {...register("horaFin", {
                                required: "La hora de fin es requerida",
                                validate: {
                                    horaFinIsAfterInicio: (horaFin, formValues) => {
                                        // turno es Nocturno (id 3)
                                        if (formValues.turnoId == 3) return true;

                                        if(formValues.horaInicio === undefined ||
                                            horaFin < formValues.horaInicio)
                                            return false;

                                        return true;
                                    }
                                }
                            })}
                            label="Hora de fin"
                            aria-invalid={errors.horaFin ? "true" : "false"}
                            type="time"/>

                        {errors.horaFin?.type === "required" && (
                            <p className="valErrorText text-sm">{errors.horaFin?.message}</p>
                        )}
                        {errors.horaFin?.type === "horaFinIsAfterInicio" && (
                            <p className="valErrorText text-sm">La hora de fin no puede ser antes que la de inicio</p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-x-2 pb-3">
                    <div>
                        <FormSelect
                            {...register("turnoId")}
                            label="Turno"
                            aria-invalid={errors.turnoId ? "true" : "false"}
                            placeholder="Seleccionar turno"
                            options={inputValues?.turnos.map((item) => ({ label: item.nombre, value: item.id})) ?? []}/>

                        {errors.turnoId?.type === "min" && (
                            <p className="valErrorText text-sm">{"El turno es requerido"}</p>
                        )}
                    </div>
                </div>

                {errorMessage && (
                    <div className="pb-3">
                        <ErrorMessage>
                            {errorMessage}
                        </ErrorMessage>
                    </div>
                )}

                <ActionButtons
                    isLoading={isLoading}
                    disabledSaveButton={!isDirty}
                    saveButtonText="Actualizar"
                    saveButtonType="submit"
                    onCancel={onCancel}/>
            </form>
        </FormContainer>
    );
}
